/**
 * useAdminNotifications  (singleton shared store)
 * ─────────────────────
 * Polls Supabase every 20s across every table the admin cares about, aggregates
 * an unread count, and lets multiple <AdminNotificationBell/> mounts (desktop
 * + mobile) subscribe to the SAME data without double-polling or double-title-
 * writes.
 *
 * Why not Supabase Realtime websockets?
 *   1. No need to touch `alter publication supabase_realtime add table …` on
 *      production Supabase — this works the moment the code ships.
 *   2. Latency of ≤20s is fine for admin follow-up (goal is to prevent 3-day
 *      Annapurna-style silence, not shave milliseconds off).
 *   3. Robust to browser tab suspension, network flaps, and RLS misconfig.
 *
 * Public shape returned to callers:
 *   {
 *     totalUnread,                 // integer
 *     unreadByTable: { [table]: n },
 *     recent: [{ id, table, tab, label, title, sub, created_at }],
 *     loading, lastFetchedAt,
 *     markSeen(tableKey),          // clear that table's unread count
 *     markAllSeen(),
 *     refresh(),                    // force-poll immediately
 *   }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const POLL_INTERVAL_MS = 20_000;
const RECENT_LIMIT_PER_TABLE = 6;
const DROPDOWN_LIMIT = 15;
const LS_KEY = 'eager_admin_notif_last_seen_v1';

// Every table the admin cares about + how to render each row and where to
// route on click. Keep in sync with AdminDashboard tab keys.
export const NOTIF_SOURCES = [
    {
        table: 'partnership_requests', tab: 'partnership-requests',
        label: 'Partnership', accent: 'orange',
        columns: 'id, created_at, college_name, contact_name',
        title: (r) => r.college_name || 'Institution',
        sub:   (r) => r.contact_name ? `Contact — ${r.contact_name}` : 'New partnership request',
    },
    {
        table: 'contact_messages', tab: 'messages',
        label: 'Message', accent: 'sky',
        columns: 'id, created_at, name, email, message',
        title: (r) => r.name || r.email || 'Anonymous',
        sub:   (r) => (r.message || '').slice(0, 60) + ((r.message || '').length > 60 ? '…' : ''),
    },
    {
        table: 'counseling_bookings', tab: 'counseling-bookings',
        label: 'Booking', accent: 'emerald',
        columns: 'id, created_at, full_name, phone, topic',
        title: (r) => r.full_name || 'Unknown',
        sub:   (r) => r.topic ? `${r.topic} · ${r.phone || ''}` : (r.phone || 'Booking'),
    },
    {
        table: 'leads', tab: 'leads',
        label: 'Ad Lead', accent: 'rose',
        columns: 'id, created_at, name, phone, course_interest',
        title: (r) => r.name || 'Lead',
        sub:   (r) => [r.course_interest, r.phone].filter(Boolean).join(' · '),
    },
    {
        table: 'notify_signups', tab: 'notify-signups',
        label: 'Notify me', accent: 'amber',
        columns: 'id, created_at, email, source, vertical',
        title: (r) => r.email || 'Email',
        sub:   (r) => [r.source, r.vertical].filter(Boolean).join(' · '),
    },
    {
        table: 'eager_minds_v2', tab: 'eager-minds',
        label: 'Eager Minds', accent: 'indigo',
        columns: 'id, created_at, name, title',
        title: (r) => r.name || 'Submission',
        sub:   (r) => r.title || 'New submission',
    },
    {
        table: 'grant_claims', tab: 'grant-claims',
        label: 'Grant Claim', accent: 'purple',
        columns: 'id, created_at, full_name, college_name',
        title: (r) => r.full_name || 'Claim',
        sub:   (r) => r.college_name || 'Grant claim',
    },
    {
        table: 'users', tab: 'signups',
        label: 'New Signup', accent: 'cyan',
        columns: 'id, joined_at, name, email, phone',
        title: (r) => r.name || r.email || r.phone || 'User',
        sub:   (r) => r.email || r.phone || 'Signup',
        createdAtCol: 'joined_at',  // users table uses joined_at not created_at
    },
];

// ─── SINGLETON STORE ───────────────────────────────────────────────────
// Two <AdminNotificationBell/> mounts (desktop navbar + mobile navbar) share
// one polling loop + one document.title writer. Without this, we'd (a) run
// two independent fetch loops against Supabase (doubling quota) and (b) both
// bells would race to write document.title producing "(2) (2) Eager".
const store = {
    counts: {},
    rows: {},
    lastSeen: {},
    lastFetchedAt: null,
    loading: false,
    _timer: null,
    _inflight: false,
    _listeners: new Set(),
    _visibilityBound: false,
    _hydrated: false,
};

function loadLastSeenFromDisk() {
    if (typeof window === 'undefined') return {};
    try {
        return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}') || {};
    } catch { return {}; }
}

function persistLastSeen() {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(store.lastSeen)); } catch { /* quota */ }
}

function notifyAll() { store._listeners.forEach((fn) => fn()); }

async function pollOnce() {
    if (store._inflight) return;
    store._inflight = true;
    store.loading = true;
    notifyAll();
    try {
        const results = await Promise.all(NOTIF_SOURCES.map(async (src) => {
            const createdCol = src.createdAtCol || 'created_at';
            const since = store.lastSeen[src.table] || null;
            try {
                let q = supabase
                    .from(src.table)
                    .select(src.columns, { count: 'exact' })
                    .order(createdCol, { ascending: false })
                    .limit(RECENT_LIMIT_PER_TABLE);
                if (since) q = q.gt(createdCol, since);
                const { data, count, error } = await q;
                if (error) throw error;
                return { table: src.table, count: count || 0, rows: data || [] };
            } catch (e) {
                // Table missing / RLS block / network — silently skip; the
                // bell simply won't show that source's count. Enable
                // `window.__EAGER_NOTIF_DEBUG = true` in devtools to inspect.
                if (typeof window !== 'undefined' && window.__EAGER_NOTIF_DEBUG) console.warn('[notifs]', src.table, e?.message);
                return { table: src.table, count: 0, rows: [] };
            }
        }));
        const counts = {};
        const rows = {};
        for (const r of results) { counts[r.table] = r.count; rows[r.table] = r.rows; }
        store.counts = counts;
        store.rows = rows;
        store.lastFetchedAt = Date.now();
    } finally {
        store._inflight = false;
        store.loading = false;
        notifyAll();
    }
}

function ensurePollerStarted() {
    if (store._timer) return;
    if (!store._hydrated) {
        store.lastSeen = loadLastSeenFromDisk();
        store._hydrated = true;
    }
    if (!store._visibilityBound && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') pollOnce();
        });
        store._visibilityBound = true;
    }
    // Wire document.title writer ONCE at singleton bootstrap. This runs
    // regardless of how many <AdminNotificationBell/> instances are mounted
    // (desktop + mobile navbars = 2 mounts) so the "(N) Eager" prefix is
    // never doubled.
    if (!store._titleBound && typeof document !== 'undefined') {
        let originalTitle = document.title.replace(/^\(\d+\)\s*/, '');
        store._listeners.add(function updateTitle() {
            // Refresh the "clean" base title in case some other code changed it
            // (e.g., route change, page-level Helmet). Strip our own prefix.
            const currentClean = document.title.replace(/^\(\d+\)\s*/, '');
            if (currentClean !== originalTitle) originalTitle = currentClean;
            const total = Object.values(store.counts).reduce((a, b) => a + (b || 0), 0);
            const target = total > 0 ? `(${total > 99 ? '99+' : total}) ${originalTitle}` : originalTitle;
            if (document.title !== target) document.title = target;
        });
        store._titleBound = true;
    }
    pollOnce();  // initial fetch
    const loop = () => {
        store._timer = window.setTimeout(async () => {
            if (typeof document === 'undefined' || document.visibilityState === 'visible') await pollOnce();
            loop();
        }, POLL_INTERVAL_MS);
    };
    loop();
}

// ─── PUBLIC HOOK ───────────────────────────────────────────────────────
export function useAdminNotifications({ enabled }) {
    const [, force] = useState(0);

    useEffect(() => {
        if (!enabled) return undefined;
        ensurePollerStarted();
        const rerender = () => force((x) => x + 1);
        store._listeners.add(rerender);
        return () => { store._listeners.delete(rerender); };
    }, [enabled]);

    const totalUnread = useMemo(
        () => Object.values(store.counts).reduce((a, b) => a + (b || 0), 0),
        // lastFetchedAt bumps whenever the store updates, so this memo
        // recomputes on every poll — no deep-equality needed.
        [store.counts, store.lastFetchedAt]
    );

    const recent = useMemo(() => {
        const merged = [];
        for (const src of NOTIF_SOURCES) {
            const createdCol = src.createdAtCol || 'created_at';
            for (const r of (store.rows[src.table] || [])) {
                merged.push({
                    id: `${src.table}:${r.id}`,
                    table: src.table,
                    tab: src.tab,
                    label: src.label,
                    accent: src.accent,
                    title: src.title(r),
                    sub: src.sub(r),
                    created_at: r[createdCol] || r.created_at,
                });
            }
        }
        merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return merged.slice(0, DROPDOWN_LIMIT);
    }, [store.rows, store.lastFetchedAt]);

    const markSeen = useCallback((tableKey) => {
        const now = new Date().toISOString();
        store.lastSeen = { ...store.lastSeen, [tableKey]: now };
        persistLastSeen();
        store.counts = { ...store.counts, [tableKey]: 0 };
        notifyAll();
    }, []);

    const markAllSeen = useCallback(() => {
        const now = new Date().toISOString();
        const next = {};
        for (const src of NOTIF_SOURCES) next[src.table] = now;
        store.lastSeen = next;
        persistLastSeen();
        store.counts = {};
        notifyAll();
    }, []);

    return {
        totalUnread,
        unreadByTable: store.counts,
        recent,
        loading: store.loading,
        lastFetchedAt: store.lastFetchedAt,
        markSeen,
        markAllSeen,
        refresh: pollOnce,
    };
}
